import { useTranslation } from "@redotech/redo-i18n";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { formatDateTime } from "@redotech/redo-web/time";
import { memo, type ReactNode } from "react";
import * as styles from "./flow-editor-top-header.module.css";
import { FlowVersionHistory } from "./flow-version-history";

interface FlowEditorTopHeaderProps {
  title: string;
  publishedAt: Date | undefined | null;
  buttons: ReactNode;
  flowType: FlowType;
  showVersionHistory?: boolean;
  /**
   * Override the Active/Inactive badge state. Used by the AdvancedFlow editor
   * which has no draft/published concept — flows are active iff `enabled` is
   * true on the document. When omitted, falls back to the publishedAt check.
   */
  active?: boolean;
  /** Explicit status label for editors with draft/published revisions. */
  status?: "active" | "draft" | "inactive" | "previous";
  /**
   * When true, hides the "Last published" timestamp line. AdvancedFlow has
   * no publish concept.
   */
  hideLastPublished?: boolean;
}

export const FlowEditorTopHeader = memo(function FlowEditorTopHeader({
  title,
  publishedAt,
  buttons,
  flowType,
  showVersionHistory,
  active,
  status,
  hideLastPublished,
}: FlowEditorTopHeaderProps) {
  const { t } = useTranslation();
  const resolvedStatus =
    status ?? ((active ?? !!publishedAt) ? "active" : "inactive");
  const isActive = resolvedStatus === "active";
  const statusLabel =
    resolvedStatus === "active"
      ? t("Active")
      : resolvedStatus === "draft"
        ? t("Draft")
        : resolvedStatus === "previous"
          ? t("Previous version")
          : t("Inactive");

  return (
    <div className={styles.header}>
      <Flex align="center" justify="space-between" w="full">
        <Flex dir="column" gap="xs">
          <Text fontSize="lg" fontWeight="bold">
            {title}
          </Text>
          <Flex align="center" gap="sm">
            <RedoBadge
              color={isActive ? "success" : "gray"}
              size="xs"
              text={statusLabel}
            />
            {publishedAt && !hideLastPublished && (
              <Text fontSize="xs" textColor="secondary">
                {resolvedStatus === "previous"
                  ? t("Published: {{dateTime}}", {
                      dateTime: formatDateTime(
                        Temporal.Instant.fromEpochMilliseconds(
                          publishedAt.getTime(),
                        ),
                      ),
                    })
                  : t("Last published: {{dateTime}}", {
                      dateTime: formatDateTime(
                        Temporal.Instant.fromEpochMilliseconds(
                          publishedAt.getTime(),
                        ),
                      ),
                    })}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex align="center" gap="sm">
          {showVersionHistory && <FlowVersionHistory flowType={flowType} />}
          {buttons}
        </Flex>
      </Flex>
    </div>
  );
});
